import React, { Component } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { rgba } from 'polished';
import { createSuiTheme } from '@splashthat/sui/lib/theme';
import { SuiContainer, SuiButton, SuiField } from '@splashthat/sui';

import Image from 'common/icons/Image';
import ImagePicker from 'common/components/ImagePicker';
import ImageUploader from 'common/compounds/ImageUploader';
import ColorPickerPopover from '../ColorPicker/ColorPickerPopover';

const TRANSPARENT_COLOR = {
    name: 'transparent',
    value: /rgba\([0-9]*, [0-9]*, [0-9]*, 0\)/,
    style: {
        background: 'url("https://d24wuq6o951i2g.cloudfront.net/img/transparent.png")',
        backgroundSize: '80px',
        borderColor: rgba(createSuiTheme().palette.grayscale.black, 0),
    },
};

const BASE_CLASS_NAME = 'ColorAndImagePicker';
export default class ColorAndImagePicker extends Component {
    static propTypes = {
        colorList: PropTypes.array.isRequired,
        color: PropTypes.object.isRequired,
        label: PropTypes.string.isRequired,
        changeColor: PropTypes.func.isRequired,
        imageUrl: PropTypes.string,
        imageBaseUrl: PropTypes.string,
        onChangeImage: PropTypes.func.isRequired,
        onEditImage: PropTypes.func.isRequired,
        qaId: PropTypes.string.isRequired,
        getAnchorEl: PropTypes.func,
        getRefWidth: PropTypes.func,
    };

    static defaultProps = {
        getAnchorEl: noop,
        getRefWidth: noop,
        imageUrl: '',
        imageBaseUrl: '',
    };

    state = {
        popoverIsVisible: false,
        hasImage: this.props.imageUrl && this.props.imageBaseUrl,
        isImageUploaderVisible: false,
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        return {
            ...prevState,
            hasImage: nextProps.imageUrl && nextProps.imageBaseUrl,
        };
    }

    showPopover = (e) => {
        e && e.stopPropagation();
        this.setState((prevState) => ({
            ...prevState,
            popoverIsVisible: !this.state.popoverIsVisible,
        }));
    };

    showImageUploader = () => {
        this.setState((prevState) => ({
            ...prevState,
            isImageUploaderVisible: true,
        }));
    };

    closeImageUploader = () => {
        this.setState((prevState) => ({
            ...prevState,
            isImageUploaderVisible: false,
        }));
    };

    getBackgroundStyle = (color) =>
        color === TRANSPARENT_COLOR.name || TRANSPARENT_COLOR.value.test(color)
            ? TRANSPARENT_COLOR.style
            : {
                  backgroundColor: color,
              };

    uploadImageAndCloseUploader = (url) => {
        this.props.onChangeImage(url);
        this.props.onEditImage(url);
        this.closeImageUploader();
    };

    showColorPickerPopover = () => (
        <ColorPickerPopover
            showPopover={this.state.popoverIsVisible}
            minWidth={this.props.getRefWidth()}
            colorList={this.props.colorList}
            anchorEl={this.props.getAnchorEl()}
            onClickClose={this.showPopover}
            onClickChangeColor={this.props.changeColor}
            colorPickerColor={this.props.color}
            colorPickerLabel={this.props.label}
            getBackgroundStyle={this.getBackgroundStyle}
            qaId={`${this.props.qaId}--popover`}
        />
    );

    render() {
        const { color, imageUrl, imageBaseUrl, onChangeImage, onEditImage, qaId } = this.props;
        const { popoverIsVisible } = this.state;

        return (
            <React.Fragment>
                <SuiField
                    fullWidth
                    className={`${BASE_CLASS_NAME}__container`}
                    formElement={
                        <SuiContainer
                            className={`${BASE_CLASS_NAME}__subcontainer`}
                            variation={`flex-group flex-${this.state.hasImage ? 'vertical' : 'horizontal'}`}
                            data-qa={`${qaId}--container`}
                        >
                            <button
                                className={`${BASE_CLASS_NAME}__selector`}
                                onClick={this.showPopover}
                                data-qa={`${qaId}--color-button`}
                            >
                                <div
                                    style={this.getBackgroundStyle(color.value)}
                                    className={`${BASE_CLASS_NAME}__selector--color`}
                                />
                            </button>
                            {this.state.hasImage ? (
                                <SuiContainer className={`${BASE_CLASS_NAME}__ImagePicker`}>
                                    <ImagePicker
                                        imageUrl={imageUrl}
                                        imageBaseUrl={imageBaseUrl}
                                        onChangeImage={onChangeImage}
                                        onEditImage={onEditImage}
                                        qaId={qaId}
                                    />
                                </SuiContainer>
                            ) : (
                                <div className={`${BASE_CLASS_NAME}__image-uploader`} data-qa={`${qaId}--image-button`}>
                                    <SuiButton
                                        icon={Image}
                                        size="small"
                                        variation="navigation"
                                        onClick={this.showImageUploader}
                                    />
                                </div>
                            )}
                        </SuiContainer>
                    }
                />
                {popoverIsVisible ? this.showColorPickerPopover() : null}
                <ImageUploader
                    open={this.state.isImageUploaderVisible}
                    onImageSelected={this.uploadImageAndCloseUploader}
                    closeImageUploader={this.closeImageUploader}
                />
            </React.Fragment>
        );
    }
}
